import { Link } from 'react-router';
import type { LucideIcon } from 'lucide-react';
import {
  PackagePlus,
  Ticket,
  Calendar,
  Megaphone,
  RefreshCw,
} from 'lucide-react';
import { SectionCard } from '@/components/ui';

interface QuickAction {
  id: string;
  label: string;
  description: string;
  to: string;
  icon: LucideIcon;
  accent: string;
}

interface QuickActionsPanelProps {
  title: string;
  description?: string;
  labels: {
    newProduct: string;
    newProductHint: string;
    newCoupon: string;
    newCouponHint: string;
    newEvent: string;
    newEventHint: string;
    newNotice: string;
    newNoticeHint: string;
    processClaim: string;
    processClaimHint: string;
  };
}

export const QuickActionsPanel = ({ title, description, labels }: QuickActionsPanelProps) => {
  const actions: ReadonlyArray<QuickAction> = [
    {
      id: 'new-product',
      label: labels.newProduct,
      description: labels.newProductHint,
      to: '/products/new',
      icon: PackagePlus,
      accent: 'var(--cta-primary)',
    },
    {
      id: 'new-coupon',
      label: labels.newCoupon,
      description: labels.newCouponHint,
      to: '/coupons?create=1',
      icon: Ticket,
      accent: 'var(--status-success-fg)',
    },
    {
      id: 'new-event',
      label: labels.newEvent,
      description: labels.newEventHint,
      to: '/cms/events/new',
      icon: Calendar,
      accent: 'var(--status-warn-fg)',
    },
    {
      id: 'new-notice',
      label: labels.newNotice,
      description: labels.newNoticeHint,
      to: '/cms/notices/new',
      icon: Megaphone,
      accent: 'var(--status-info-fg)',
    },
    {
      id: 'process-claim',
      label: labels.processClaim,
      description: labels.processClaimHint,
      to: '/claims?claimStatus=REQUESTED',
      icon: RefreshCw,
      accent: 'var(--status-danger-fg)',
    },
  ];

  return (
    <SectionCard
      title={title}
      {...(description ? { description } : {})}
      data-testid="quick-actions-panel"
    >
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <li key={action.id}>
              <Link
                to={action.to}
                className="group flex items-start gap-3 rounded-[var(--radius-button)] p-3 transition-all hover:bg-[color:var(--surface-1)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
                style={{ border: '1px solid transparent' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--divider-strong)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                }}
                data-testid={`quick-action-${action.id}`}
              >
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-105"
                  style={{
                    background: 'var(--surface-1)',
                    color: action.accent,
                  }}
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm font-semibold"
                    style={{ color: 'var(--content-strong)' }}
                  >
                    {action.label}
                  </p>
                  <p
                    className="truncate text-[11px]"
                    style={{ color: 'var(--content-muted)' }}
                  >
                    {action.description}
                  </p>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
};
